import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { VOICES, MODELS, CHAT_MODELS, COUNTRIES } from './paygData';

const usd = (n, d = 3) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })}`;

const Pick = ({ label, value, onChange, options, id }) => (
  <label className="py-pick"><span>{label}</span>
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={`estimator-${id}`}>
      {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  </label>
);

const Slider = ({ label, value, onChange, max, step, id, unit }) => (
  <label className="py-slider"><span>{label}<b data-testid={`estimator-${id}-value`}>{value.toLocaleString()} {unit}</b></span>
    <input type="range" min="0" max={max} step={step} value={value} onChange={e => onChange(+e.target.value)}
      style={{ '--p': `${(value / max) * 100}%` }} data-testid={`estimator-${id}`} />
  </label>
);

export const CostEstimator = () => {
  const [minutes, setMinutes] = useState(1000);
  const [chats, setChats] = useState(0);
  const [voice, setVoice] = useState(VOICES[0].id);
  const [model, setModel] = useState(MODELS[0].id);
  const [country, setCountry] = useState(COUNTRIES[0].id);
  const [chatModel, setChatModel] = useState(CHAT_MODELS[0].id);
  const [kb, setKb] = useState(false);

  const r = useMemo(() => {
    const v = VOICES.find(x => x.id === voice).rate;
    const m = MODELS.find(x => x.id === model).rate;
    const t = COUNTRIES.find(x => x.id === country).rate;
    const a = kb ? 0.005 : 0;
    const perMin = v + m + t + a;
    const c = CHAT_MODELS.find(x => x.id === chatModel).rate;
    return { v, m, t, a, c, perMin, voiceTotal: perMin * minutes, chatTotal: chats * c };
  }, [voice, model, country, kb, minutes, chats, chatModel]);

  return (
    <section id="calculator" className="py-section" data-testid="payg-estimator">
      <div className="hm-container">
        <h2 className="py-h2">Estimate your cost</h2>
        <div className="py-est">
          <div className="py-est-in">
            <Slider id="minutes" label="Call minutes per month" unit="min" value={minutes} onChange={setMinutes} max={50000} step={100} />
            <Slider id="chats" label="AI chat messages per month" unit="msgs" value={chats} onChange={setChats} max={50000} step={100} />
            <div className="py-picks">
              <Pick id="voice" label="Voice" value={voice} onChange={setVoice} options={VOICES} />
              <Pick id="model" label="AI model" value={model} onChange={setModel} options={MODELS} />
              <Pick id="country" label="Phone calls to" value={country} onChange={setCountry} options={COUNTRIES} />
              <Pick id="chat-model" label="Chat AI model" value={chatModel} onChange={setChatModel} options={CHAT_MODELS} />
              <button type="button" className={`py-toggle py-full${kb ? ' on' : ''}`} onClick={() => setKb(!kb)} data-testid="estimator-kb">
                <i />Knowledge base<small>+$0.005/session</small>
              </button>
            </div>
          </div>
          <div className="py-est-out" data-testid="estimator-result">
            <p className="py-est-label">Estimated monthly cost</p>
            <p className="py-est-total" data-testid="estimator-total">{usd(r.voiceTotal + r.chatTotal, 2)}</p>
            <div className="py-est-rate"><span>Per minute</span><b data-testid="estimator-per-minute">{usd(r.perMin)}</b></div>
            <ul>
              <li><span>Voice engine</span><span>{usd(r.v)}</span></li>
              <li><span>AI model</span><span>{usd(r.m, r.m < 0.01 ? 4 : 3)}</span></li>
              <li><span>Phone carrier</span><span>{usd(r.t)}</span></li>
              <li className={kb ? '' : 'off'} data-testid="estimator-kb-line"><span>Knowledge base</span><span>{kb ? usd(r.a) : '–'}</span></li>
              <li className={chats > 0 ? '' : 'off'} data-testid="estimator-chat-line"><span>AI chat{chats > 0 ? ` · ${chats.toLocaleString()} msgs` : ''}</span><span>{chats > 0 ? usd(r.chatTotal, 2) : '–'}</span></li>
            </ul>
            <Link to="/signup?plan=launchpad" className="py-btn py-btn-light" data-testid="estimator-start">Start with $10 free</Link>
            <Link to="/enterprise" className={`py-est-ent${minutes >= 20000 ? '' : ' off'}`} aria-hidden={minutes < 20000} tabIndex={minutes >= 20000 ? 0 : -1} data-testid="estimator-enterprise">Running this volume? Get Enterprise pricing →</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostEstimator;
